import type {
  CampaGift,
  EligibilityResult,
  GiftStatus,
  GiftTypeConfig,
  Merchant,
  Redemption,
  Transferability,
} from '../types/campagift'

/* ------------------------------------------------------------------ *
 * MONEY. All CampaGift arithmetic is integer kobo. No floats, ever.
 * ------------------------------------------------------------------ */

export function koboToNaira(kobo: number): string {
  const negative = kobo < 0
  const abs = Math.abs(Math.trunc(kobo))
  const major = Math.trunc(abs / 100)
  const minor = abs % 100
  const grouped = major.toLocaleString('en-NG')
  const body = minor === 0 ? grouped : `${grouped}.${String(minor).padStart(2, '0')}`
  return `${negative ? '−' : ''}₦${body}`
}

export function nairaToKobo(input: string): number | null {
  const cleaned = input.replace(/[₦,\s]/g, '')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  const [major, minor = ''] = cleaned.split('.')
  return Number(major) * 100 + Number(minor.padEnd(2, '0'))
}

/** Basis points applied to an integer amount, rounded half-up, never negative. */
export function applyBps(amountKobo: number, bps: number): number {
  if (bps <= 0) return 0
  return Math.min(amountKobo, Math.round((amountKobo * bps) / 10_000))
}

/* ------------------------------------------------------------------ *
 * DERIVED VALUE. Remaining value is computed from the redemption
 * history — there is no mutable `gift.balance` anywhere.
 * ------------------------------------------------------------------ */

/**
 * Sums EVERY redemption record on the instrument, including ones later marked
 * reversed. History is additive: a reversal is its own record carrying the
 * negative amount, so the pair cancels. Excluding the original as well would
 * refund the value twice.
 */
export function redeemedKobo(gift: CampaGift, redemptions: Redemption[]): number {
  return redemptions
    .filter((r) => r.giftId === gift.id)
    .reduce((total, r) => total + r.amountKobo, 0)
}

export function remainingKobo(gift: CampaGift, redemptions: Redemption[]): number {
  /* Clamped at zero: remaining value can never go negative. */
  return Math.max(0, gift.faceValueKobo - redeemedKobo(gift, redemptions))
}

export function isExpired(gift: CampaGift, now = new Date()): boolean {
  if (!gift.expiresAt) return false
  return new Date(gift.expiresAt).getTime() < now.getTime()
}

export function daysUntilExpiry(gift: CampaGift, now = new Date()): number | null {
  if (!gift.expiresAt) return null
  const ms = new Date(gift.expiresAt).getTime() - now.getTime()
  return Math.ceil(ms / 86_400_000)
}

/**
 * Status is derived from the instrument's own facts, so it can never drift
 * away from its redemption history. Administrative states (frozen, suspended,
 * cancelled, disputed, refunded) are terminal for this computation and win.
 */
const STICKY: GiftStatus[] = [
  'frozen',
  'suspended',
  'cancelled',
  'reversed',
  'refunded',
  'disputed',
  'closed',
  'draft',
]

export function deriveStatus(
  gift: CampaGift,
  redemptions: Redemption[],
  now = new Date(),
): GiftStatus {
  if (STICKY.includes(gift.status)) return gift.status
  if (gift.acceptance === 'pending') return 'issued'
  if (gift.acceptance === 'declined') return 'closed'
  const remaining = remainingKobo(gift, redemptions)
  if (remaining === 0) return 'fully_redeemed'
  /* Expiry does not erase value; it only stops redemption. */
  if (isExpired(gift, now)) return 'expired'
  if (remaining < gift.faceValueKobo) return 'partially_redeemed'
  return 'available'
}

export const STATUS_LABEL: Record<GiftStatus, string> = {
  draft: 'Draft',
  issued: 'Awaiting acceptance',
  available: 'Available',
  partially_redeemed: 'Partially redeemed',
  fully_redeemed: 'Fully redeemed',
  expired: 'Expired',
  suspended: 'Suspended',
  frozen: 'Frozen',
  cancelled: 'Cancelled',
  reversed: 'Reversed',
  refunded: 'Refunded',
  disputed: 'Disputed',
  closed: 'Closed',
}

export function statusTone(status: GiftStatus): 'ok' | 'warn' | 'danger' | 'neutral' | 'brand' {
  if (status === 'available') return 'ok'
  if (status === 'partially_redeemed') return 'brand'
  if (status === 'fully_redeemed' || status === 'closed') return 'neutral'
  if (status === 'frozen' || status === 'suspended' || status === 'disputed') return 'danger'
  return 'warn'
}

export const TRANSFER_LABEL: Record<Transferability, string> = {
  non_transferable: 'Non-transferable — only the assigned holder may redeem',
  transferable: 'Transferable — remaining value may be passed on',
  merchant_bound: 'Merchant-bound — usable only with the named merchant',
  category_bound: 'Category-bound — usable only within the named category',
}

/* ------------------------------------------------------------------ *
 * CODES. A gift code is a credential: unpredictable, stored hashed,
 * never derived from the instrument id, and rate limited on use.
 * ------------------------------------------------------------------ */

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

export function generateGiftCode(): string {
  const bytes = new Uint32Array(12)
  crypto.getRandomValues(bytes)
  const body = Array.from(bytes, (n) => ALPHABET[n % ALPHABET.length]).join('')
  return `${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8, 12)}`
}

/** Stand-in for the server-side password-grade hash. Never reversible here. */
export function hashCode(code: string): string {
  let h1 = 0x811c9dc5
  let h2 = 0x01000193
  for (let i = 0; i < code.length; i += 1) {
    h1 = (h1 ^ code.charCodeAt(i)) * 0x01000193
    h2 = (h2 + code.charCodeAt(i) * (i + 7)) ^ (h1 >>> 5)
  }
  return `cg$${(h1 >>> 0).toString(16)}${(h2 >>> 0).toString(16)}`
}

export function verifyCode(code: string, hash: string): boolean {
  return hashCode(code.trim().toUpperCase()) === hash
}

export function maskReference(reference: string): string {
  return `${reference.slice(0, 6)}••••${reference.slice(-3)}`
}

/* ------------------------------------------------------------------ *
 * ELIGIBILITY ENGINE. One evaluator, driven entirely by configuration.
 * No screen decides whether a redemption is permitted.
 * ------------------------------------------------------------------ */

interface EligibilityInput {
  gift: CampaGift
  type: GiftTypeConfig
  merchant: Merchant
  redemptions: Redemption[]
  /** Amount requested, when validating a specific redemption. */
  requestedKobo?: number
  /** Category of the goods being paid for, for category/product restrictions. */
  purchaseCategoryId?: string
  now?: Date
}

export function evaluateEligibility({
  gift,
  type,
  merchant,
  redemptions,
  requestedKobo,
  purchaseCategoryId,
  now = new Date(),
}: EligibilityInput): EligibilityResult {
  const checks: EligibilityResult['checks'] = []
  const remaining = remainingKobo(gift, redemptions)
  const status = deriveStatus(gift, redemptions, now)

  const add = (rule: string, passed: boolean, detail: string) =>
    checks.push({ rule, passed, detail })

  add(
    'Instrument status',
    status === 'available' || status === 'partially_redeemed',
    status === 'available' || status === 'partially_redeemed'
      ? `${STATUS_LABEL[status]} — redeemable`
      : `${STATUS_LABEL[status]} — not redeemable`,
  )
  add(
    'Risk state',
    gift.riskState !== 'frozen',
    gift.riskState === 'clear'
      ? 'No open risk hold'
      : gift.riskState === 'flagged'
        ? 'Flagged for review — redemption still permitted'
        : 'Frozen pending investigation',
  )
  add(
    'Expiry',
    !isExpired(gift, now),
    gift.expiresAt
      ? isExpired(gift, now)
        ? `Expired ${gift.expiresAt}`
        : `Valid until ${gift.expiresAt}`
      : 'No expiry on this gift type',
  )
  add(
    'Remaining value',
    remaining > 0,
    `${koboToNaira(remaining)} remaining of ${koboToNaira(gift.faceValueKobo)}`,
  )
  add(
    'Merchant participation',
    merchant.campaGiftEnabled && merchant.participation === 'active',
    merchant.campaGiftEnabled && merchant.participation === 'active'
      ? `${merchant.name} accepts CampaGift`
      : `${merchant.name} is not currently accepting CampaGift`,
  )

  const merchantAllowed =
    type.eligibleMerchantIds === 'all'
      ? true
      : type.eligibleMerchantIds.includes(merchant.id)
  add(
    'Merchant eligibility',
    merchantAllowed && merchant.eligibleTypeIds.includes(type.id),
    merchantAllowed
      ? merchant.eligibleTypeIds.includes(type.id)
        ? `${type.name} is accepted here`
        : `${merchant.name} is not configured for ${type.name}`
      : `${type.name} is restricted to specific merchants`,
  )

  const categoryTarget = purchaseCategoryId ?? merchant.categoryId
  const categoryAllowed =
    type.eligibleCategoryIds === 'all' || type.eligibleCategoryIds.includes(categoryTarget)
  add(
    'Category eligibility',
    categoryAllowed,
    type.eligibleCategoryIds === 'all'
      ? 'No category restriction'
      : categoryAllowed
        ? `${categoryTarget} is within scope`
        : `Restricted to ${type.eligibleCategoryIds.join(', ')} — ${categoryTarget} is not eligible`,
  )

  /* Redemption ceiling is the tightest of every configured rule. */
  const perTransactionCap = type.maxRedemptionPerTransactionKobo ?? remaining
  const spentToday = redemptions
    .filter((r) => r.giftId === gift.id && r.at.slice(0, 10) === now.toISOString().slice(0, 10))
    .reduce((total, r) => total + r.amountKobo, 0)
  const dailyHeadroom =
    type.maxDailyRedemptionKobo === null
      ? remaining
      : Math.max(0, type.maxDailyRedemptionKobo - spentToday)
  const merchantCap = merchant.dailyRedemptionCapKobo ?? remaining
  const maxRedeemableKobo = Math.max(
    0,
    Math.min(remaining, perTransactionCap, dailyHeadroom, merchantCap),
  )

  if (!type.partialRedemption) {
    add(
      'Partial redemption',
      false,
      'This gift type must be redeemed in a single full transaction',
    )
  }

  if (requestedKobo !== undefined) {
    add(
      'Requested amount',
      requestedKobo >= type.minRedemptionKobo,
      `Minimum redemption is ${koboToNaira(type.minRedemptionKobo)}`,
    )
    add(
      'Amount within limits',
      requestedKobo > 0 && requestedKobo <= maxRedeemableKobo,
      requestedKobo > maxRedeemableKobo
        ? `Exceeds the ${koboToNaira(maxRedeemableKobo)} redeemable here right now`
        : `${koboToNaira(requestedKobo)} is within every applicable limit`,
    )
    if (!type.partialRedemption) {
      add(
        'Full redemption required',
        requestedKobo === remaining,
        `Must redeem the full ${koboToNaira(remaining)}`,
      )
    }
  }

  return {
    eligible: checks.every((check) => check.passed),
    checks,
    maxRedeemableKobo,
  }
}

export function commissionFor(
  amountKobo: number,
  type: GiftTypeConfig,
  merchant: Merchant,
): { commissionKobo: number; netKobo: number; bps: number } {
  /* Merchant-specific rate wins over the gift type default. */
  const bps = merchant.commissionBps ?? type.merchantCommissionBps
  const commissionKobo = applyBps(amountKobo, bps)
  return { commissionKobo, netKobo: amountKobo - commissionKobo, bps }
}

export function bpsLabel(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)}%`
}
